<template>
  <div class="settings-view">
    <BaseModal>
      <BaseConfirmationModal :text="`Are you sure you want to delete ${selectedEntity.name || 'this'} entity?`"
                             @yes="handleDeleteEntity" />
    </BaseModal>

    <h1 class="text-center">
      Settings
    </h1>

    <div class="content-wrapper flex">
      <div>
        <section class="entities-section">
          <h2>
            Entities
          </h2>

          <div class="add-entity flex m-t-16">
            <BaseInput name="name"
                       :value="entity"
                       @input="handleNewEntity"
                       placeholder="new entity" />
            <button cy="add-entity-btn"
                    :disabled="!entity"
                    @click="addEntity">
              Add entity
            </button>
          </div>

          <ul class="entities-list m-t-24"
              cy="entities-list">
            <li v-for="(entity, index) in entities"
                :key="index"
                class="entity-card">

              <div class="card-header flex flex-between align-center">
                <h4 class="capitalize"
                    :class="{'unnamed': !entity.name}">
                  {{ entity.name || 'Unnamed entity' }}
                </h4>
                <i @click="handleDeleteModal(entity)"
                   class="delete-entity pointer">
                  <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.616 16C2.168 16 1.78667 15.8426 1.472 15.528C1.15733 15.2133 1 14.8323 1 14.385V1.99998H0V0.99998H4V0.22998H10V0.99998H14V1.99998H13V14.385C13 14.845 12.846 15.2293 12.538 15.538C12.23 15.8466 11.8453 16.0006 11.384 16H2.616ZM12 1.99998H2V14.385C2 14.5643 2.05767 14.7116 2.173 14.827C2.28833 14.9423 2.436 15 2.616 15H11.385C11.5383 15 11.6793 14.936 11.808 14.808C11.9367 14.68 12.0007 14.5386 12 14.384V1.99998ZM4.808 13H5.808V3.99998H4.808V13ZM8.192 13H9.192V3.99998H8.192V13Z" fill="#FD7474"/>
                  </svg>
                </i>
              </div>

              <span v-if="entity.lastScrapedAt"
                    class="last-scrape">
                last scraped: {{ format(new Date(entity.lastScrapedAt), 'dd MMM., yyyy') }}
              </span>

              <div class="card-controls">
                <BaseSelect name="city"
                            :value="entity.city"
                            @select="handleCity($event, entity._id)"
                            :options="usCities" />

                <div class="cadence-group">
                  <label :for="`${cadence}-${entity._id}`"
                         v-for="(cadence, index) in cadences"
                         :key="index"
                         class="pointer">
                    <input type="radio"
                           :name="entity._id"
                           :id="`${cadence}-${entity._id}`"
                           :checked="entity.cadence === cadence"
                           @change="handleCadence({id: entity._id, cadence})">
                    <span>{{ cadence }}</span>
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <div class="right-side">
        <h2 class="appearance-title m-b-16">
          Appearance
        </h2>
        <section class="legend-list">
          <h2>
            Legend
          </h2>
          <ul class="m-t-16">
            <li v-for="(list, key) in tags"
                :key="key"
                class="m-b-16">
              <h4 class="capitalize m-b-8">
                {{ key }}
                <span v-if="key === 'control'">
                  <input
                      class="pointer"
                      type="color"
                      v-model="selectedColorControlTags"
                      @change="handleColorChangeControlTags"
                  >
                </span>
              </h4>
              <ul>
                <li v-for="(tag, index) in list"
                    :key="index"
                    class="flex align-center m-b-4">
                  <i v-html="icons[tag.icon]"
                     :class="key" />
                  <span class="capitalize m-l-8">
                    {{ tag.name }}
                  </span>
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <section class="color-manager m-t-24">
          <h4>
            Change background color
          </h4>
          <div class="color-picker-content m-t-16 flex align-center">
            <input
                class="pointer"
                type="color"
                v-model="selectedColor"
                @change="handleColorChange"
            >
            <span class="m-l-8 pointer" @click="handleColorReset">
              Reset color
            </span>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {useStore} from "vuex";
  import {computed, ref} from "vue";
  import { format } from 'date-fns';
  import BaseInput from "@/components/BaseInput.vue";
  import {capitalizeWords} from "@/store/utils";
  import BaseModal from "@/components/BaseModal.vue";
  import BaseConfirmationModal from "@/components/BaseConfirmationModal.vue";
  import BaseSelect from "@/components/BaseSelect.vue";
  import {icons, usCities} from "@/store/data";

  const { state, dispatch, commit } = useStore()
  dispatch('get_entities')
  const entity = computed(() => state.settings.newEntity);
  const entities = computed(() => state.settings.entities)
  const selectedEntity = computed(() => state.settings.selectedEntity)
  const cadences = computed(() => state.settings.cadences)
  const tags = {
    sentiment: [
      {name: 'Positive', icon: 'positive'},
      {name: 'Neutral', icon: 'neutral'},
      {name: 'Negative', icon: 'negative'},
    ],
    control: [
      {name: 'full control', icon: 'full_control'},
      {name: 'partial control', icon: 'partial_control'},
      {name: 'no control', icon: 'no_control'},
    ],
    entity: [
      {name: 'not this entity', icon: 'not_this_entity'}
    ]
  }
  const selectedColor = ref(localStorage.getItem('bg-color') || '#E7DDD3');
  const selectedColorControlTags = ref(localStorage.getItem('control-tags-color') || '#2762F3');
  const defaultColor = '#E7DDD3';

  function handleColorReset () {
    document.documentElement.style.setProperty('--bg-color', defaultColor);
    selectedColor.value = defaultColor;
    localStorage.setItem('bg-color', defaultColor);
  }

  function handleColorChange ({target: {value}}) {
    document.documentElement.style.setProperty('--bg-color', value);
    localStorage.setItem('bg-color', value);
  }

  function handleColorChangeControlTags ({target: {value}}) {
    document.documentElement.style.setProperty('--tags-control-color', value);
    localStorage.setItem('control-tags-color', value);
  }

  function handleNewEntity ({value}) {
    commit('SET_SETTINGS_STATE', {key: 'newEntity', data: capitalizeWords(value)})
  }

  function addEntity () {
    dispatch('add_entity')
  }

  function handleDeleteModal (entity) {
    commit('SET_MODAL', true)
    commit('SET_SELECTED_ENTITY', entity)
  }

  function handleDeleteEntity () {
    dispatch('delete_entity', selectedEntity.value._id)
  }

  function handleCadence ({id, cadence}) {
    dispatch('update_entity_details', {id, payload: {cadence}})
  }

  function handleCity ({name, value}, id) {
    dispatch('update_entity', {id, payload: {[name]: value}})
  }
</script>

<style lang="scss">
  @import "../styles/vars";

  .settings-view {
    @media only screen and (min-width: 0) {
      padding: 0 48px;
      height: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      .content-wrapper {
        margin-top: 24px;
        flex: 1 1 0;
        min-height: 0;
        justify-content: flex-start;
        gap: 48px;
        > div:first-child {
          display: flex;
          flex-direction: column;
          min-height: 0;
          flex: 1 1 0;
          min-width: 0;
        }
        .right-side {
          flex: none;
          width: 340px;
          align-self: flex-start;
          max-height: 100%;
          overflow-y: auto;
          padding: 24px;
          background-color: $white;
          border-radius: 16px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
        }
      }
      .legend-list {
        h4 span {
          display: inline-flex;
          vertical-align: middle;
          margin-left: 8px;
          input[type="color"] {
            width: 24px;
            height: 24px;
            padding: 0;
            border: none;
            background: none;
            display: block;
          }
        }
        i {
          transform: translateY(2px);
          &.control svg path {
            fill: var(--tags-control-color);
          }
        }
      }
      .entities-section {
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        min-height: 0;
        > div {
          align-items: flex-end;
        }
        button {
          height: 48px;
          flex: none;
          white-space: nowrap;
          margin-left: 16px;
          padding: 0 24px;
          border-radius: 8px;
          font-size: 1.5rem;
          font-weight: 600;
          background-color: $green;
          color: white;
        }
        .add-entity {
          justify-content: center;
          .base-input {
            flex: 0 1 672px;
            min-width: 0;
          }
          input {
            width: 100%;
            height: 48px;
            padding: 0 18px;
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 8px;
            font-size: 1.6rem;
            text-transform: capitalize;
            &:focus {
              outline: none;
              border-color: $green;
            }
            &::placeholder {
              text-transform: lowercase;
            }
          }
        }

        .entities-list {
          box-sizing: content-box;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(420px, 100%), 1fr));
          gap: 32px;
          align-content: start;
          flex: 1 1 0;
          min-height: 0;
          overflow-y: auto;
          padding: 0 16px 8px 0;
          li {
            padding: 24px 28px;
            background-color: $white;
            border-radius: 16px;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
            h4 {
              color: #1a1a1a;
              font-size: 2.1rem;
              font-weight: 700;
              &.unnamed {
                opacity: 0.5;
              }
            }
            .last-scrape {
              margin-top: 4px;
              opacity: 0.5;
              display: inline-block;
              font-size: 1.4rem;
            }
            .card-controls {
              margin-top: 16px;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
            .delete-entity {
              display: inline-flex;
              &:hover {
                transform: scale(1.1);
              }
            }
            .cadence-group {
              display: flex;
              gap: 20px;
              margin-top: 16px;
              label {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 1.6rem;
                color: #1a1a1a;
              }
              input {
                margin: 0;
                accent-color: #1a1a1a;
              }
            }
          }
        }
      }
      .base-select select {
        border: none;
        background: transparent;
        padding: 0;
        font-size: 1.7rem;
        color: #1a1a1a;
      }
    }
    @media only screen and (max-width: 900px) {
      .content-wrapper {
        flex-direction: column;
        overflow-y: auto;
        > div:first-child {
          flex: 1 0 320px;
        }
        .entities-list {
          grid-template-columns: 1fr;
        }
        .right-side {
          width: auto;
          align-self: stretch;
          max-height: none;
          overflow-y: visible;
        }
      }
    }
  }
</style>